import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Users,
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Heart,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Clients() {
  const { organization } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterServiceType, setFilterServiceType] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    nhs_number: '',
    service_type: 'domiciliary',
    address: '',
    phone: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    notes: '',
  });

  useEffect(() => {
    fetchClients();
  }, [organization, filterServiceType]);

  const fetchClients = async () => {
    try {
      const params = { organization_id: organization?.id };
      if (filterServiceType !== 'all') {
        params.service_type = filterServiceType;
      }
      const response = await axios.get(`${API_URL}/api/clients`, { params });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/clients`, {
        ...newClient,
        organization_id: organization?.id,
      });
      toast.success('Client created successfully');
      setIsDialogOpen(false);
      setNewClient({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        nhs_number: '',
        service_type: 'domiciliary',
        address: '',
        phone: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        notes: '',
      });
      fetchClients();
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error('Failed to create client');
    }
  };

  const filteredClients = clients.filter(client => {
    const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) ||
           client.nhs_number?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getServiceTypeBadge = (type) => {
    const colors = {
      domiciliary: 'bg-blue-100 text-blue-700 border-blue-200',
      supported_living: 'bg-purple-100 text-purple-700 border-purple-200',
      care_home: 'bg-teal-100 text-teal-700 border-teal-200',
    };
    const labels = {
      domiciliary: 'Domiciliary',
      supported_living: 'Supported Living',
      care_home: 'Care Home',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${colors[type] || colors.domiciliary}`}>
        {labels[type] || type}
      </span>
    );
  };

  return (
    <div className="min-h-screen" data-testid="clients-page">
      <Header title="Clients & Residents" />
      
      <div className="p-6 space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or NHS number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="client-search"
              />
            </div>
            <Select value={filterServiceType} onValueChange={setFilterServiceType}>
              <SelectTrigger className="w-44" data-testid="service-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="domiciliary">Domiciliary</SelectItem>
                <SelectItem value="supported_living">Supported Living</SelectItem>
                <SelectItem value="care_home">Care Home</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Client</DialogTitle>
                <DialogDescription>
                  Enter the client's details to add them to the system
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateClient} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      value={newClient.first_name}
                      onChange={(e) => setNewClient(prev => ({ ...prev, first_name: e.target.value }))}
                      required
                      data-testid="client-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      value={newClient.last_name}
                      onChange={(e) => setNewClient(prev => ({ ...prev, last_name: e.target.value }))}
                      required
                      data-testid="client-last-name"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth *</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={newClient.date_of_birth}
                      onChange={(e) => setNewClient(prev => ({ ...prev, date_of_birth: e.target.value }))}
                      required
                      data-testid="client-dob"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nhs_number">NHS Number</Label>
                    <Input
                      id="nhs_number"
                      placeholder="XXX XXX XXXX"
                      value={newClient.nhs_number}
                      onChange={(e) => setNewClient(prev => ({ ...prev, nhs_number: e.target.value }))}
                      data-testid="client-nhs"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="service_type">Service Type *</Label>
                  <Select 
                    value={newClient.service_type} 
                    onValueChange={(value) => setNewClient(prev => ({ ...prev, service_type: value }))}
                  >
                    <SelectTrigger data-testid="client-service-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="domiciliary">Domiciliary Care</SelectItem>
                      <SelectItem value="supported_living">Supported Living</SelectItem>
                      <SelectItem value="care_home">Care Home</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={newClient.address}
                    onChange={(e) => setNewClient(prev => ({ ...prev, address: e.target.value }))}
                    data-testid="client-address"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={newClient.phone}
                    onChange={(e) => setNewClient(prev => ({ ...prev, phone: e.target.value }))}
                    data-testid="client-phone"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_name">Emergency Contact Name</Label>
                    <Input
                      id="emergency_contact_name"
                      value={newClient.emergency_contact_name}
                      onChange={(e) => setNewClient(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                      data-testid="client-emergency-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_phone">Emergency Contact Phone</Label>
                    <Input
                      id="emergency_contact_phone"
                      type="tel"
                      value={newClient.emergency_contact_phone}
                      onChange={(e) => setNewClient(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                      data-testid="client-emergency-phone"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={newClient.notes}
                    onChange={(e) => setNewClient(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    data-testid="client-notes"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-client">
                    Add Client
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Clients</p>
                  <p className="text-3xl font-bold">{clients.length}</p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Domiciliary</p>
                  <p className="text-3xl font-bold">
                    {clients.filter(c => c.service_type === 'domiciliary').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Heart className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Care Home</p>
                  <p className="text-3xl font-bold">
                    {clients.filter(c => c.service_type === 'care_home').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Clients Table */}
        <Card data-testid="clients-table-card">
          <CardHeader>
            <CardTitle>All Clients</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 bg-slate-100 animate-pulse rounded" />
                ))}
              </div>
            ) : filteredClients.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>NHS Number</TableHead>
                    <TableHead>Service Type</TableHead>
                    <TableHead>Date of Birth</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow key={client.id} className="cursor-pointer hover:bg-slate-50" data-testid={`client-row-${client.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-teal-700">
                              {client.first_name?.[0]}{client.last_name?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{client.first_name} {client.last_name}</p>
                            {client.address && (
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {client.address}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{client.nhs_number || '-'}</TableCell>
                      <TableCell>{getServiceTypeBadge(client.service_type)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {client.date_of_birth}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {client.phone && (
                            <p className="text-xs flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {client.phone}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={client.status === 'active' ? 'default' : 'secondary'}>
                          {client.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No clients found</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setIsDialogOpen(true)}
                >
                  Add Your First Client
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
